import React, { useState, useEffect, useMemo } from 'react';
import { WebUI } from "../../wui-core/src/index"
import { WithConfigConsumer } from '../../wui-provider/src/context';
import RcTree from './Tree';
import TreeNode from './TreeNode';
import { TreeProps, TreeData } from './iTree';
import { isLazyLoad, filterTreeData } from './utils/util';

const defaultProps = {
    clsPrefix: 'wui-tree',
    checkable: false,
    showIcon: false,
}

function isUndefined(value: any) {
    return value === undefined
}

/**
 * Tree 组件的函数式实现
 * 处理 filterValue 筛选功能
 */
const TreeFunctional: React.FC<TreeProps> = (props) => {
    const mergedProps = { ...defaultProps, ...props };

    // 状态管理
    const [treeData, setTreeData] = useState<TreeData[] | undefined>(mergedProps.treeData);
    const [filterValue, setFilterValue] = useState<string | undefined>(mergedProps.filterValue);
    const [originTreeData, setOriginTreeData] = useState<TreeData[] | undefined>(mergedProps.treeData);

    // 处理 props 变化
    useEffect(() => {
        // 处理 filterValue 和 treeData 的变化
        if ('filterValue' in mergedProps
            && 'treeData' in mergedProps
            && mergedProps.treeData
            && mergedProps.filterValue !== undefined
            && (mergedProps.filterValue !== filterValue || originTreeData !== mergedProps.treeData)) {

            const filteredData = filterTreeData(
                mergedProps.treeData,
                mergedProps.filterValue as string,
                mergedProps
            );
            setTreeData(filteredData);
            setFilterValue(mergedProps.filterValue as string);
            setOriginTreeData(mergedProps.treeData);
        } else if ('treeData' in mergedProps && mergedProps.treeData !== originTreeData) {
            // 仅 treeData 变化
            setTreeData(mergedProps.treeData);
            setOriginTreeData(mergedProps.treeData);
        }
    }, [mergedProps.filterValue, mergedProps.treeData]);

    // 准备传递给 RcTree 的属性
    const {
        rootClassName,
        className,
        rootStyle,
        style,
        virtual,
        lazyLoad,
        children,
        checkable,
        clsPrefix,
        ...restProps
    } = mergedProps;

    // 计算额外属性
    const extraProps = useMemo(() => ({
        className: isUndefined(className) ? rootClassName : className,
        style: isUndefined(style) ? rootStyle : style,
        lazyLoad: isUndefined(lazyLoad)
            ? (isUndefined(virtual) ? isLazyLoad(treeData) : virtual)
            : lazyLoad,
    }), [className, rootClassName, style, rootStyle, lazyLoad, virtual, treeData]);

    // 处理 checkable 属性
    const processedCheckable = checkable
        ? (<span className={`${clsPrefix}-checkbox-inner`}/>)
        : checkable;

    return (
        <RcTree
            {...extraProps}
            {...restProps}
            clsPrefix={clsPrefix}
            checkable={processedCheckable}
            treeData={treeData}
        >
            {children}
        </RcTree>
    );
};

// 应用装饰器
const Tree = WithConfigConsumer({name: "tree"})(
    WebUI({name: "tree", defaultProps})(TreeFunctional)
) as any;

export type TreeType = typeof Tree
type TreeNodeType = typeof TreeNode

export interface TreeInterface extends TreeType {
    TreeNode: TreeNodeType
}

const TreeWrapper = Tree as TreeInterface
TreeWrapper.TreeNode = TreeNode
export default TreeWrapper
